import { Box, Button, Card, CardContent, Tab } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import InteractionsTable from "./InteractionsTable";
import { ContactInteraction, InteractionEvent, InteractionReason, InteractionSource, InteractionType } from "./types";
import Iconify from "../../../../minimals/components/iconify";
import AddOrEditInteractionModal from "./AddOrEditInteractionModal";
import { useUserContext } from "../../../contexts/UserContext";
import { useColleagues } from "../../hooks/useColleagues";

export default function Interactions() {
  const { user } = useUserContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const contactId = searchParams.get("id");

  const { colleagues } = useColleagues(contactId || "");

  const [contactInteractions, setContactInteractions] = useState<ContactInteraction[]>([]);
  const [interactionTypeOptions, setInteractionTypeOptions] = useState<InteractionType[]>([]);
  const [interactionReasonOptions, setInteractionReasonOptions] = useState<InteractionReason[]>([]);
  const [interactionEventOptions, setInteractionEventOptions] = useState<InteractionEvent[]>([]);
  const [interactionSourceOptions, setInteractionSourceOptions] = useState<InteractionSource[]>([]);
  const [interactionToEditOrAdd, setInteractionToEditOrAdd] = useState<ContactInteraction>();

  const getAndSetInteractions = async (contactId: string) => {
    if (!contactId) return;
    const { data } = await axios.get<ContactInteraction[]>(
      `/api/clientphonebook/contactInteractions/getAllByContactId/${contactId}`
    );
    const filteredInteractions = data.filter((interaction) => interaction.type && interaction.type.name !== "Skipped");
    setContactInteractions(filteredInteractions);
  };

  const handleSubmit = async (contactInteraction: ContactInteraction, colleagueIds?: number[], eventName?: string) => {
    if (eventName) {
      const { data: event } = await axios.post<InteractionEvent>(
        `/api/clientphonebook/contactInteractions/getOrCreateInteractionEvent`,
        { name: eventName }
      );
      setInteractionEventOptions((options) =>
        options.some((option) => option.id === event.id) ? options : [...options, event]
      );
      contactInteraction = { ...contactInteraction, eventId: event.id };
    }

    const { clientCultivator, additionalAttendee, ...interactionDBData } = contactInteraction;

    const data = await axios.post(`/api/clientphonebook/contactInteractions/Upsert`, {
      interaction: {
        ...interactionDBData,
        clientCultivatorId: clientCultivator?.id,
        additionalAttendeeId: additionalAttendee?.id,
        contactId
      },
      colleagueIds: colleagueIds || []
    });

    getAndSetInteractions(contactId!);
  };

  const deleteInteraction = async (interactionId: number) => {
    const data = await axios.delete(`/api/clientphonebook/contactInteractions/delete/${interactionId}`);
    getAndSetInteractions(contactId!);
  };

  const initializeNewInteraction = () => {
    const newInteraction = {
      id: 0,
      contactId: Number(contactId || 0),
      clientCultivator: user?.internalUser, ///default the cultivator to the current user
      typeId: null,
      interactionDate: new Date().toISOString(),
      notes: "",
      followUps: []
    } as ContactInteraction;
    setInteractionToEditOrAdd(newInteraction);
  };

  useEffect(() => {
    if (!contactId) return;

    const getInteractionsAndOptions = async () => {
      try {
        const [interactionsResponse, typesResponse, eventsResponse, reasonsResponse, sourcesResponse] = await Promise.all([
          axios.get(`/api/clientphonebook/contactInteractions/getAllByContactId/${contactId}`),
          axios.get(`/api/clientphonebook/contactInteractions/getInteractionTypes`),
          axios.get(`/api/clientphonebook/contactInteractions/getInteractionEvents`),
          axios.get(`/api/clientphonebook/contactInteractions/getInteractionReasons`),
          axios.get(`/api/clientphonebook/contactInteractions/getInteractionSources`)
        ]);

        const filteredInteractions = interactionsResponse.data.filter(
          (interaction: ContactInteraction) => interaction.type && interaction.type.name !== "Skipped"
        );
        setContactInteractions(filteredInteractions);
        const filteredTypeOptions = typesResponse.data.filter((type: InteractionType) => type.name !== "Skipped");
        setInteractionTypeOptions(filteredTypeOptions);
        setInteractionEventOptions(eventsResponse.data);
        setInteractionReasonOptions(reasonsResponse.data);
        setInteractionSourceOptions(sourcesResponse.data);
      } catch (error) {
        console.error("Error fetching interaction data:", error);
      }
    };

    getInteractionsAndOptions();
  }, [contactId]);

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 3.5 }}>
            <Tab
              label={`${contactInteractions.length} interaction${contactInteractions.length != 1 ? "s" : ""}`}
              value="orders"
            />
            {/* {isContactExportViewer(user) && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  sx={{ marginLeft: "auto" }}
                  onClick={() => downloadVcfByContactIds([Number(contactId)])}
                >
                  Export Contact Now
                </Button>
              </Box>
            )} */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                startIcon={<Iconify icon="eva:plus-fill" />}
                variant="contained"
                sx={{ marginLeft: "auto" }}
                onClick={() => initializeNewInteraction()}
              >
                Add Interaction
              </Button>
            </Box>
          </Box>
          <InteractionsTable
            interactions={contactInteractions}
            onDeleteInteraction={async (id) => {
              await deleteInteraction(id);
            }}
            onEditInteraction={(interaction) => setInteractionToEditOrAdd(interaction)}
          />
        </CardContent>
      </Card>
      {interactionToEditOrAdd && (
        <AddOrEditInteractionModal
          open
          onClose={() => setInteractionToEditOrAdd(undefined)}
          onSubmit={handleSubmit}
          interactionTypeOptions={interactionTypeOptions}
          interactionReasonOptions={interactionReasonOptions}
          interactionEventOptions={interactionEventOptions}
          interactionSourceOptions={interactionSourceOptions}
          interaction={interactionToEditOrAdd}
          setInteraction={setInteractionToEditOrAdd}
          colleagues={colleagues}
        />
      )}
    </>
  );
}
